"use client";

import React from 'react';
import Link from 'next/link';

const CommitmentLevelSection = () => {
  const models = [
    {
      title: "Quick Assessment",
      timeline: "2-6 weeks",
      bestFor: "Companies exploring EDW feasibility",
      timelineDetail: "2-6 weeks",
      deliverables: [
        "Data landscape audit",
        "EDW feasibility report",
        "ROI projection",
        "Implementation roadmap"
      ],
      pricing: "Fixed-price project",
      cta: "Start Assessment",
      featured: false
    },
    {
      title: "Embedded Data Scientist",
      timeline: "2+ months",
      bestFor: "Teams needing ongoing data expertise",
      timelineDetail: "2+ months (flexible)",
      deliverables: [
        "Dedicated data scientist",
        "Weekly progress reports",
        "Custom analytics solutions",
        "Team knowledge transfer"
      ],
      pricing: "Monthly retainer",
      cta: "Hire Expert",
      featured: true
    },
    {
      title: "Full EDW Implementation",
      timeline: "6-18 months",
      bestFor: "Enterprise-wide data transformation",
      timelineDetail: "6-18 months (phased)",
      deliverables: [
        "Complete EDW infrastructure",
        "ETL pipelines",
        "Executive dashboards",
        "Training & documentation"
      ],
      pricing: "Phased milestone billing",
      cta: "Plan Implementation",
      featured: false
    }
  ];

  return (
    <section className="bg-[#F8F6F3] py-12 sm:py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Header */}
        <div className="text-left md:text-center mb-10 sm:mb-12 md:mb-16">
          <p className="text-gold text-xs font-semibold tracking-[0.15em] uppercase mb-3 sm:mb-4">
            FLEXIBLE ENGAGEMENT
          </p>
          <h2 className="font-serif text-navy text-[26px] sm:text-[34px] md:text-[44px] lg:text-[52px] leading-[1.2] font-normal mb-4 sm:mb-6">
            Choose Your <span className="italic">Commitment Level</span>
          </h2>
          <p className="text-charcoal text-[15px] sm:text-base md:text-lg leading-[1.7] max-w-2xl md:mx-auto">
            Start small, scale as you see results. No forced long-term contracts.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {models?.map((model, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 transition-all duration-300 relative ${
                model?.featured
                  ? 'border-2 border-[#B8956A] shadow-[0_8px_32px_rgba(184,149,106,0.15)]'
                  : 'border border-gray-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)]'
              }`}
            >
              {/* Most Popular Badge */}
              {model?.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-[#B8956A] to-[#D4AF7A] text-navy text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">
                    MOST POPULAR
                  </span>
                </div>
              )}

              {/* Title */}
              <h3 className="text-navy text-xl sm:text-2xl font-serif font-normal mb-2 mt-0 sm:mt-2">
                {model?.title}
              </h3>

              {/* Timeline Badge */}
              <p className="text-gold text-sm font-medium mb-4 sm:mb-6">{model?.timeline}</p>

              {/* Best For */}
              <div className="mb-6">
                <p className="text-[#8B8B8B] text-xs font-semibold uppercase tracking-wider mb-2">
                  BEST FOR
                </p>
                <p className="text-charcoal text-sm leading-[1.6]">{model?.bestFor}</p>
              </div>

              {/* Timeline */}
              <div className="mb-6">
                <p className="text-[#8B8B8B] text-xs font-semibold uppercase tracking-wider mb-2">
                  TIMELINE
                </p>
                <p className="text-charcoal text-sm font-medium">{model?.timelineDetail}</p>
              </div>

              {/* Deliverables */}
              <div className="mb-6">
                <p className="text-[#8B8B8B] text-xs font-semibold uppercase tracking-wider mb-3">
                  DELIVERABLES
                </p>
                <ul className="space-y-2.5">
                  {model?.deliverables?.map((deliverable, i) => (
                    <li key={i} className="flex items-start text-charcoal text-sm leading-[1.6]">
                      <span className="text-gold mr-2.5 flex-shrink-0 mt-0.5">◦</span>
                      <span>{deliverable}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pricing */}
              <div className="mb-8">
                <p className="text-[#8B8B8B] text-xs font-semibold uppercase tracking-wider mb-2">
                  PRICING
                </p>
                <p className="text-navy text-base font-semibold">{model?.pricing}</p>
              </div>

              {/* CTA Button */}
              <Link
                href="/contact"
                className={`inline-block w-full min-h-[44px] px-6 py-3.5 rounded-lg font-semibold text-sm transition-all duration-300 active:scale-[0.98] text-center ${
                  model?.featured
                    ? 'bg-gradient-to-r from-[#B8956A] to-[#D4AF7A] text-navy hover:shadow-[0_4px_16px_rgba(184,149,106,0.3)] hover:-translate-y-0.5'
                    : 'bg-white border-2 border-navy text-navy hover:bg-navy hover:text-white'
                }`}
              >
                {model?.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CommitmentLevelSection;